import { useTranslation } from "react-i18next";
import {
  AsyncCallArgs,
  GridContainer,
  GridItem,
  handleSubmitValidationFail,
  InputFormField,
  SelectFormField,
  isNotEmpty,
  useAsyncCall,
  separatePath,
  FormSection,
  FileUploadButton,
  ScrollableAsyncFormAutocomplete,
  useReactHookForm,
} from "@duosoftbg/nacid-components";
import { ViewDialog, CircularLoader } from "@duosoftbg/nacid-components";
import { FormProvider } from "react-hook-form";
import { toast } from "react-toastify";
import React, { useEffect, useState } from "react";

import { Alert } from "@mui/material";
import useAppSelector from "../../../../../hooks/redux/base/useAppSelector";
import useAppDispatch from "../../../../../hooks/redux/base/useAppDispatch";
import {
  buildFetchFileUrl,
  createNationalUniversity,
  getNationalUniversity,
  getSettlementsAutocomplete,
  updateNationalUniversity,
  uploadFileRestricted,
} from "../../../../../axios/api/services";
import { NationalUniversityDetails } from "../../../../../types/nationalUniversityTypes";
import { nationalUniversityInitialValues } from "../../../../../init/nationalUniversityInitValues";
import { createNationalUniversityValidationSchema } from "../../../../../yup/schema/nationalUniversity/nationalUniversityValidationSchema";
import { closeModal } from "../../../../../store/redux/slice/ComponentsControl/nationalUniversityControl";
import Box from "@mui/system/Box";
import FileDetails from "../../../common/file/FileDetails";

const Content = ({ loading, error, methods, id, separatedPath, setSeparatedPath, asyncCall, selectedSettlement }) => {
  const { t } = useTranslation();

  const options = [
    { value: true, text: t("l.yes") },
    { value: false, text: t("l.no") },
  ];

  if (loading) {
    return <CircularLoader />;
  }

  if (error) {
    return <Alert severity="error">{t("m.error.serverFetchingError")}</Alert>;
  }

  const onFileChange = async (e) => {
    const uploadLogo: AsyncCallArgs = {
      withGlobalBackdrop: true,
      reactHooksForm: { methods },
      promise: uploadFileRestricted(e.target.files[0], "img"),
      onSuccess: (response) => {
        const file = { ...response };
        setSeparatedPath({ relativePath: file.relativePath, fileId: file.fileId, rootDirectory: file.rootDirectory });
        methods.setValue("file", nationalUniversityInitialValues.file);
        methods.clearErrors("file");
        methods.setValue("logoRelativePath", `/${file.relativePath}/${file.fileId}`);
      },
      onValidationError: () => {
        methods.setValue("file", nationalUniversityInitialValues.file);
      },
    };
    asyncCall(uploadLogo);
  };

  return (
    <FormProvider {...methods}>
      <form>
        <FormSection label={"t.main.details"}>
          <GridContainer spacing={3} mt={0}>
            <GridItem sm={2} md={2}>
              {id !== "" && (
                <InputFormField isDisabled={true} required={true} fieldName={"id"} labelCode={"l.nomenclature.id"} />
              )}
              {id === "" && <InputFormField required={true} fieldName={"id"} labelCode={"l.nomenclature.id"} />}
            </GridItem>

            <GridItem sm={2} md={2}>
              <SelectFormField
                required={true}
                fieldName={"isActive"}
                labelCode={"l.nomenclature.active"}
                addEmptyOption={false}
                selectOptions={options}
              />
            </GridItem>
            <GridItem sm={2} md={2}>
              <InputFormField required={true} fieldName={"website"} labelCode={"l.nomenclature.website"} />
            </GridItem>
            <GridItem sm={2} md={2}>
              <InputFormField required={true} fieldName={"zipCode"} labelCode={"l.nomenclature.zipCode"} />
            </GridItem>
            <GridItem sm={4} md={4}>
              <ScrollableAsyncFormAutocomplete
                onlyActive
                key={`settlements-autocomplete-key-${selectedSettlement != null ? selectedSettlement.id : ""}`}
                fieldName={"settlement.id"}
                selectedOption={selectedSettlement}
                setOptionText={(option) => option.optionName}
                getOptionLabel={(option) => option.id}
                setInputOnSelect={(option) => option.selectedOptionValue}
                autocompleteFn={getSettlementsAutocomplete}
                label={"l.settlement"}
                reduceOptionObject={false}
              />
            </GridItem>
            <GridItem sm={12} md={12}>
              <InputFormField required={true} fieldName={"name"} labelCode={"l.nomenclature.name"} />
            </GridItem>
            <GridItem sm={12} md={12}>
              <InputFormField required={true} fieldName={"nameEn"} labelCode={"l.nomenclature.nameEn"} />
            </GridItem>
            <GridItem sm={12} md={12}>
              <InputFormField required={true} fieldName={"address"} labelCode={"l.nomenclature.address"} />
            </GridItem>
            <GridItem sm={12} md={12}>
              <InputFormField required={true} fieldName={"addressEn"} labelCode={"l.nomenclature.addressEn"} />
            </GridItem>
          </GridContainer>
        </FormSection>
        <FormSection label={"t.logo"}>
          <GridContainer spacing={3} mt={0}>
            <GridItem sm={12} md={12}>
              {separatedPath !== null && (
                <Box
                  component="img"
                  sx={{
                    height: 200,
                    width: 200,
                  }}
                  alt="University logo empty"
                  src={buildFetchFileUrl(separatedPath.relativePath, separatedPath.fileId, separatedPath.rootDirectory)}
                />
              )}
            </GridItem>
            <GridItem sm={12} md={12}>
              <div style={{ marginTop: "10px" }}>
                <FileUploadButton
                  accept={"image/jpeg,image/jpg,image/png,image/gif"}
                  onFileChange={onFileChange}
                  id="logo-upload"
                />
                <FileDetails hint={"m.file.img.allowed.type.message"} uploadError={methods.formState.errors?.file} />
              </div>
            </GridItem>
          </GridContainer>
        </FormSection>
      </form>
    </FormProvider>
  );
};

const SaveNationalUniversityDialog = ({ filterFn }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { asyncCall } = useAsyncCall();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { open, id } = useAppSelector((state) => {
    return state.ComponentsControl.nationalUniversityControl.modals.edit;
  });

  const handleCloseDialog = () => {
    dispatch(closeModal({ modalType: "edit" }));
    setSelectedSettlement(null);
    setSeparatedPath(null);
  };

  const { methods } = useReactHookForm<NationalUniversityDetails>({
    defaultValues: nationalUniversityInitialValues,
    validationSchema: createNationalUniversityValidationSchema,
  });

  const [separatedPath, setSeparatedPath] = useState(null);

  const [selectedSettlement, setSelectedSettlement] = useState(null);

  useEffect(() => {
    if (open) {
      if (isNotEmpty(id)) {
        const asyncCallArgs: AsyncCallArgs = {
          promise: getNationalUniversity(id),
          onSuccess: (response) => {
            methods.reset({ ...response, file: nationalUniversityInitialValues.file });
            setSeparatedPath({
              ...(id !== "" ? separatePath(methods.getValues("logoRelativePath")) : null),
              rootDirectory: null,
            });
            setSelectedSettlement({
              id: response.settlement.id,
              name: response.settlement.name,
              optionName: response.settlement.name,
              selectedOptionValue: response.settlement.fullSettlementName,
              isActive: response.settlement.isActive,
            });
            setLoading(false);
          },
          onError: () => {
            setError(true);
            setLoading(false);
          },
        };
        asyncCall(asyncCallArgs);
      } else {
        methods.reset(nationalUniversityInitialValues);
        setLoading(false);
        setSelectedSettlement(null);
        setSeparatedPath(null);
      }
    }
    // eslint-disable-next-line
  }, [id, open]);

  const onSubmit = (values) => {
    const asyncCreation: AsyncCallArgs = {
      withGlobalBackdrop: true,
      promise: id === "" ? createNationalUniversity(values) : updateNationalUniversity(values),
      reactHooksForm: { methods },
      onSuccess: () => {
        methods.reset(nationalUniversityInitialValues, {
          keepIsSubmitted: true,
          keepSubmitCount: true,
        });
        toast.success(t("m.create.nomenclature.success"));
        handleCloseDialog();
        filterFn();
      },
    };
    asyncCall(asyncCreation);
  };

  return (
    <ViewDialog
      open={open}
      onClose={handleCloseDialog}
      title={id === "" ? "t.nomenclature.create.dialog" : id && "t.nomenclature.edit.dialog"}
      disableEnforceFocus
      dialogActionsSpacing={{ pr: 3 }}
      onSubmitBtnClick={
        !loading && !error && methods.handleSubmit(onSubmit, () => handleSubmitValidationFail(methods.formState, t, undefined))
      }
    >
      <Content
        methods={methods}
        id={id}
        loading={loading}
        error={error}
        separatedPath={separatedPath}
        setSeparatedPath={setSeparatedPath}
        asyncCall={asyncCall}
        selectedSettlement={selectedSettlement}
      />
    </ViewDialog>
  );
};

export default SaveNationalUniversityDialog;
